import * as ko from "knockout";
import {global} from "../../appContext";
import {nsPassword} from "./nsPassword";
import {isSilentPasswordExport} from "./isSilentPasswordExport";
import {_d} from "../../util/misc";
import {SyncstepId} from "../../constants/Syncstep";
import {IAnchor, IExportQueue, IObjectData} from "../../index";
import PromiseRequest from "../../req/PromiseRequest";
import {nsSync} from "../../util/nsSync";
import progressHtml from './progress.html?raw';

export const template: string = progressHtml;

const POLL_INTERVAL_MS = 7_000;
const SUBMITTED_AT_SKEW_MS = 30_000;

const ENTRA_SYNCSTEP_IDS: ReadonlyArray<number> = [
    SyncstepId.Export_Azure,
    SyncstepId.Export_Azure_2,
];

type TProgressState = 'idle' | 'polling' | 'succeeded' | 'failedFirst' | 'failedSubsequent';

type TTargetClass = 'entra' | 'onPrem' | 'other';

interface ITargetRow {
    syncstepName: string
    target: string         // anchor
    targetsystem: string
    cls: TTargetClass
    accepted: KnockoutObservable<boolean>
    failed: KnockoutObservable<boolean>
    pending: KnockoutObservable<boolean>
    lasterror: KnockoutObservable<string>
}

export interface IPasswordChangeProgressParams {
    userId: number
    submittedAt: Date
    onSucceeded?: () => void
    onFailed?: (info: { isFirst: boolean, syncstepName: string, lasterror: string }) => void
}

export class viewModel {

    private readonly userId: number;
    private readonly submittedAt: Date;
    private readonly onSucceeded: () => void;
    private readonly onFailed: (info: { isFirst: boolean, syncstepName: string, lasterror: string }) => void;
    private readonly startedAtMs: number = Date.now();

    private readonly req_getobject: PromiseRequest = global.getPromiseRequest('getobject');

    private timeoutId: number | null = null;
    private isDisposed = false;

    private readonly state: KnockoutObservable<TProgressState> = ko.observable<TProgressState>('idle');
    private readonly hasReceivedRows: KnockoutObservable<boolean> = ko.observable(false);
    private readonly rows: KnockoutObservableArray<ITargetRow> = ko.observableArray<ITargetRow>([]);

    private readonly totalCount = ko.pureComputed(() => this.rows().length);
    private readonly currentIndex = ko.pureComputed(() => {
        const arr = this.rows();
        const idx = arr.findIndex(r => !r.accepted());
        return idx === -1 ? arr.length : idx + 1;
    });
    private readonly tryingMessage = ko.pureComputed(() => {
        const x = this.currentIndex();
        const y = this.totalCount();
        if (!y) {
            return null;
        }
        return `password.progress.trying|x:${x}|y:${y}`;
    });
    private readonly pendingRows = ko.pureComputed<ITargetRow[]>(
        () => this.rows().filter(r => r.pending()),
    );
    private readonly hasPending = ko.pureComputed(() => this.pendingRows().length > 0);
    private readonly waitingOnMessage = ko.pureComputed(() => {
        const names = this.pendingRows().map(r => r.syncstepName).filter(n => !!n);
        if (!names.length) {
            return null;
        }
        // `password.progress.waitingon` uses the `|names:` placeholder so the
        // translated string can decide where to put the comma-separated list
        // (e.g. "Waiting on: AD, Entra ID").
        return `password.progress.waitingon|names:${names.join(', ')}`;
    });
    private readonly failure = ko.pureComputed(() => {
        const failed = this.rows().find(r => r.failed());
        if (!failed) {
            return null;
        }
        const isFirst = this.state() === 'failedFirst';
        return {
            isFirst,
            syncstepName: failed.syncstepName,
            lasterror: failed.lasterror() ?? '',
            headlineKey: isFirst
                ? 'password.progress.failed.first'
                : `password.progress.failed.subsequent|name:${failed.syncstepName}`,
            detailsLabelKey: 'password.progress.error.details',
        };
    });

    constructor(params: IPasswordChangeProgressParams) {
        this.userId = params.userId;
        this.submittedAt = params.submittedAt ?? new Date();
        this.onSucceeded = params.onSucceeded ?? (() => undefined);
        this.onFailed = params.onFailed ?? (() => undefined);

        this.startPolling();
    }

    private startPolling() {
        this.state('polling');
        this.scheduleNext(0);
    }

    private scheduleNext(delay: number = POLL_INTERVAL_MS) {
        if (this.isDisposed) {
            return;
        }
        this.clearTimeout();
        this.timeoutId = window.setTimeout(() => this.poll(), delay);
    }

    private clearTimeout() {
        if (this.timeoutId !== null) {
            window.clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }

    private async poll() {
        if (this.isDisposed) {
            return;
        }
        try {
            global.cacheManager.removeCache('getobject');
            this.req_getobject.abort();
            const data: IObjectData[] = await this.req_getobject.request(
                {id: this.userId, manual: false},
                false,
                {},
                {useCacheIfAvailable: false},
            );
            if (this.isDisposed) {
                return;
            }
            const obj = data?.[0];
            if (obj) {
                this.applySnapshot(obj);
            }
        } catch (err) {
            _d.warn('PasswordChangeProgress poll failed', err);
        }

        if (this.isDisposed) {
            return;
        }
        if (this.state() === 'polling') {
            this.scheduleNext();
        }
    }

    private applySnapshot(obj: IObjectData) {
        const cutoff = this.submittedAt.getTime() - SUBMITTED_AT_SKEW_MS;
        const passwordRows = (obj.exportqueue ?? []).filter(row => {
            if (!row.action || row.action.toLowerCase() !== 'password') {
                return false;
            }
            if (row.targetattribute === 'userTAP') {
                return false;
            }
            if (isSilentPasswordExport(row)) {
                return false;
            }
            const ts = nsPassword.parseServerDate(row.registered);
            return ts !== null && ts >= cutoff;
        });

        if (!passwordRows.length) {
            // If the BE doesn't produce any exportqueue rows (e.g. no PasswordSync targets
            // configured / no anchors / targetsystem resolved empty), the UI would otherwise
            // remain stuck in the "starting..." state forever. Treat this as completed after
            // a short grace period.
            const elapsedMs = Date.now() - this.startedAtMs;
            if (elapsedMs > 10_000 && this.state() === 'polling') {
                this.hasReceivedRows(true);
                this.rows([]);
                this.state('succeeded');
                this.clearTimeout();
                this.onSucceeded();
            }
            return;
        }

        this.hasReceivedRows(true);

        const sortedRows = this.sortRows(passwordRows, obj.anchor ?? []);
        this.rebuildTargetRows(sortedRows, obj.anchor ?? []);
        this.evaluateState();
    }

    private sortRows(rows: IExportQueue[], anchors: IAnchor[]): IExportQueue[] {
        const indexed = rows.map((row, i) => ({
            row,
            i,
            cls: this.classifyRow(row, anchors),
            registered: nsPassword.parseServerDate(row.registered) ?? 0,
        }));
        const order: Record<TTargetClass, number> = {entra: 0, onPrem: 1, other: 2};
        indexed.sort((a, b) => {
            const c = order[a.cls] - order[b.cls];
            if (c !== 0) return c;
            const r = a.registered - b.registered;
            if (r !== 0) return r;
            return a.i - b.i;
        });
        return indexed.map(o => o.row);
    }

    private rebuildTargetRows(rows: IExportQueue[], anchors: IAnchor[]) {
        const existing = new Map<string, ITargetRow>();
        this.rows().forEach(r => existing.set(this.rowKey(r.target, r.targetsystem), r));

        const next: ITargetRow[] = rows.map(row => {
            const key = this.rowKey(row.target, row.targetsystem);
            // Prefer the syncstep id resolved server-side (mapped from the row's
            // Target → ObjectAnchor.SyncStepId). Fall back to the anchor lookup
            // for backwards compatibility with older BE builds that don't yet
            // populate the field.
            const sourceId = (typeof row.syncstepid === 'number' && row.syncstepid > 0)
                ? row.syncstepid
                : this.lookupSourceId(row.target, anchors);
            const cls = this.classifySourceId(sourceId);
            const syncstepName = nsPassword.resolveSyncstepName(sourceId, row.syncstepname, row.targetsystem);

            const reused = existing.get(key);
            const target: ITargetRow = reused ?? {
                syncstepName,
                target: row.target ?? '',
                targetsystem: row.targetsystem ?? '',
                cls,
                accepted: ko.observable(false),
                failed: ko.observable(false),
                pending: ko.observable(true),
                lasterror: ko.observable(''),
            };

            // Refresh derived label in case translations have loaded since.
            target.syncstepName = syncstepName;
            target.cls = cls;

            const exported = !!row.exported;
            const lasterror = (row.lasterror ?? '').trim();
            const isFailed = !!lasterror;

            target.accepted(exported && !isFailed);
            target.failed(isFailed);
            target.pending(!exported && !isFailed);
            target.lasterror(isFailed ? lasterror : '');

            return target;
        });

        this.rows(next);
    }

    private evaluateState() {
        const arr = this.rows();
        if (!arr.length) {
            return;
        }

        const firstFailedIndex = arr.findIndex(r => r.failed());
        if (firstFailedIndex !== -1) {
            const failedRow = arr[firstFailedIndex];
            if (!failedRow) {
                return;
            }
            const isFirst = firstFailedIndex === 0;
            const next: TProgressState = isFirst ? 'failedFirst' : 'failedSubsequent';
            if (this.state() !== next) {
                this.state(next);
                this.clearTimeout();
                this.onFailed({
                    isFirst,
                    syncstepName: failedRow.syncstepName,
                    lasterror: failedRow.lasterror() ?? '',
                });
            }
            return;
        }

        const allAccepted = arr.every(r => r.accepted());
        if (allAccepted) {
            if (this.state() !== 'succeeded') {
                this.state('succeeded');
                this.clearTimeout();
                this.onSucceeded();
            }
        }
    }

    private classifyRow(row: IExportQueue, anchors: IAnchor[]): TTargetClass {
        const sourceId = (typeof row.syncstepid === 'number' && row.syncstepid > 0)
            ? row.syncstepid
            : this.lookupSourceId(row.target, anchors);
        return this.classifySourceId(sourceId);
    }

    private classifySourceId(sourceId: number | null): TTargetClass {
        if (sourceId !== null) {
            if (ENTRA_SYNCSTEP_IDS.includes(sourceId)) {
                return 'entra';
            }
            if (nsSync.isSyncstepOnPremAD(sourceId)) {
                return 'onPrem';
            }
        }
        return 'other';
    }

    private lookupSourceId(target: string, anchors: IAnchor[]): number | null {
        if (!target || !anchors?.length) {
            return null;
        }
        // The same anchor value can belong to more than one syncstep
        // (e.g. two Entra tenants sharing a UPN). Returning the first hit
        // would mis-attribute one of the rows, so we only trust an
        // anchor-only fallback when exactly one anchor matches. The
        // disambiguation by targetsystem is handled server-side via
        // IExportQueue.syncstepid.
        const matches = anchors.filter(a => a.anchor === target);
        const match = matches[0];
        return match ? match.sourceid : null;
    }

    private rowKey(target: string, targetsystem: string): string {
        return `${target ?? ''}|${targetsystem ?? ''}`;
    }

    public dispose() {
        this.isDisposed = true;
        this.clearTimeout();
        this.req_getobject.abort();
        global.cacheManager.removeCache('getobject');
    }

}
